import { Button, Chip, Container, HeaderBar, TypoGraph } from 'components'
import styles from './riskDashboard.module.scss'
import ReactToPrint from 'react-to-print'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { RiskDetails, RiskSummaryPanel } from './partial'
import {
    getRiskScoresDashboardV3,
    getRiskScoresSummaryV3,
    getRiskScoresDashboardAgentV3,
    getRiskScoresSummaryAgentV3
} from 'api'
import { isAgentLogin, isInsuranceLogin } from 'tools/auth'
import { reportException } from 'tools/logs'
import FilterComponent from 'components/filters'
import { Grid } from '@mui/material'
import { useDebounceInput } from 'hooks/useDebounceInput'
import { RiskScoreDashboard, RiskScoreDashboardSummary, RiskScoreDashboardDetail, CustomPagination, FilterParams } from './types/riskDashboard'
import { RiskScoreDashboardV2Response } from 'api/types/dashboard'

const MIN_SEARCH_LENGTH = 3
const MAX_SEARCH_LENGTH = 50

const RiskDashboard: React.FC = () => {
    const [riskData, setRiskData] = useState<RiskScoreDashboard | undefined>()
    const [summaryData, setSummaryData] = useState<RiskScoreDashboardSummary | undefined>()
    const [isLoading, setIsLoading] = useState(true)
    const [isSummaryLoading, setIsSummaryLoading] = useState(true)
    const [updateDate, setUpdateDate] = useState<string | undefined>()
    const [printReport, setPrintReport] = useState<boolean>(false)
    const [pagination, setPagination] = useState<CustomPagination | undefined>(undefined)
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(100)
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [sortBy, setSortBy] = useState<string | undefined>(undefined)
    const [sortDirection, setSortDirection] = useState<string | undefined>(undefined)
    const [filterParams, setFilterParams] = useState<FilterParams>({
        tagSubCategoryIds: '',
        credentialStatus: '',
        includeUntagged: false
    })

    const ref = useRef<HTMLDivElement>(null)

    const isAgent = isAgentLogin()
    const isInsurance = isInsuranceLogin()

    const fetchRiskScoreDataV3 = async (params: {
        tagSubCategoryIds?: string
        credentialStatus?: string
        includeUntagged?: boolean
        search?: string
        sortBy?: string
        sortDirection?: string
        page?: number
        size?: number
        includeSummary?: boolean
    }) => {
        setIsLoading(true)
        const apiCall = isAgent ? getRiskScoresDashboardAgentV3 : getRiskScoresDashboardV3

        try {
            const res = await apiCall({ ...params, includeSummary: false })
            if (res) {
                setRiskData(res)
                setPagination(res?.pagination)
                const hasRiskScore = res?.details?.filter((item: RiskScoreDashboardDetail) => {
                    return (item?.fleetScore && item?.fleetScore > 0)
                })
                if ((hasRiskScore?.length ?? 0) > 0) {
                    setUpdateDate(hasRiskScore?.[0]?.executionDate)
                }
            }
        } catch (error) {
            reportException(error)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchRiskScoreDataV3Summary = async (params?: FilterParams) => {
        setIsSummaryLoading(true)
        const apiCall = isAgent ? getRiskScoresSummaryAgentV3 : getRiskScoresSummaryV3

        try {
            const res = await apiCall(params)
            if (res) {
                setSummaryData(res)
            }
        } catch (error) {
            reportException(error)
        } finally {
            setIsSummaryLoading(false)
        }
    }

    const { debouncedCallback: debouncedFetchRiskScoreDataV3, cancelDebounce: cancelDebounceData } = useDebounceInput(fetchRiskScoreDataV3, 800)

    useEffect(() => {
        debouncedFetchRiskScoreDataV3({
            ...filterParams,
            search: searchQuery,
            sortBy,
            sortDirection,
            page: currentPage + 1,
            size: pageSize
        })
    }, [searchQuery])

    useEffect(() => {
        fetchRiskScoreDataV3Summary(filterParams)
    }, [filterParams])

    useEffect(() => {
        cancelDebounceData()
        fetchRiskScoreDataV3({
            ...filterParams,
            search: searchQuery,
            sortBy,
            sortDirection,
            page: currentPage + 1,
            size: pageSize
        })
    }, [currentPage, pageSize, sortBy, sortDirection, filterParams])

    const handleRiskFilterSelection = (
        selectedTags?: string[],
        includeUntagged?: boolean,
        credentialStatus?: string
    ) => {
        // Reset to first page when filters change
        setCurrentPage(0)
        setFilterParams({
            tagSubCategoryIds: selectedTags?.join(',') ?? '',
            includeUntagged,
            credentialStatus
        })
    }

    const fetchRiskScoreData = async (riskScoreCall: () => Promise<RiskScoreDashboardV2Response | undefined>) => {
        setIsLoading(true)
        try {
            const res = await riskScoreCall()
            if (res) {
                setRiskData(res)
                setSummaryData(res?.summary)
                const hasRiskScore = res?.details?.filter((item) => {
                    return (item?.fleetScore && item?.fleetScore > 0)
                })
                if ((hasRiskScore?.length ?? 0) > 0) {
                    setUpdateDate(hasRiskScore?.[0]?.executionDate)
                }
            }
        } catch (error) {
            reportException(error)
        } finally {
            setIsLoading(false)
            setIsSummaryLoading(false)
        }
    }

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page)
        setPageSize(size)
    }

    const handleSearchChange = (search: string) => {
        if (search.length > MAX_SEARCH_LENGTH) return;

        const previousSearchWasValid = searchQuery.length >= MIN_SEARCH_LENGTH
        const newSearchIsValid = search.length >= MIN_SEARCH_LENGTH

        setSearchQuery(search)

        // Only reset page if:
        // 1. We're setting a new valid search (MIN_SEARCH_LENGTH+ chars), OR
        // 2. We're clearing a previous valid search (going from MIN_SEARCH_LENGTH+ chars to empty)
        // This prevents resetting page when typing < MIN_SEARCH_LENGTH chars (which sends empty string)
        if (newSearchIsValid || (search.length === 0 && previousSearchWasValid)) {
            setCurrentPage(0)
        }
    }

    const handleSortChange = (field: string, direction: string) => {
        setSortBy(field)
        setSortDirection(direction)
        setCurrentPage(0) // Reset to first page on sort
    }

    const HeaderDom = () => {
        return (
            <Container className={styles.headerContainer}>
                <Container className={styles.headerTitleContainer}>
                    <TypoGraph
                        variant="h1"
                        sx={{ marginRight: '1rem' }}
                        mb={0}
                        content="Risk Analysis"
                    />
                    {updateDate && (
                        <Chip
                            label={`Updated ${moment(updateDate).format('dddd, MMMM Do')}`}
                            variant="filled"
                            sx={{
                                backgroundColor: '#E1FAEB',
                                color: '#20693D',
                            }}
                        />
                    )}
                </Container>
                <Container className={styles.btnContainer}>
                    <ReactToPrint
                        trigger={() => <Button label="Export Report" />}
                        content={() => ref.current}
                        onBeforeGetContent={() => setPrintReport(true)}
                        onAfterPrint={() => setPrintReport(false)}
                        documentTitle={`Risk Analysis Report`}
                    />
                </Container>
            </Container>
        )
    }

    return (
        <Container>
            <HeaderBar dom={<HeaderDom />} />
            <div ref={ref}>
                {isInsurance && <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} display={"flex"} alignItems="start" flexDirection={"column"}>
                        <FilterComponent
                            onFetchRiskScoreData={fetchRiskScoreData}
                            onHandleFilterSelection={handleRiskFilterSelection}
                            riskScoresFilters={true}
                            carriersFilters={true}
                        />
                    </Grid>
                </Grid>}
                <RiskSummaryPanel
                    summaryData={summaryData}
                    print={printReport}
                    isLoading={isSummaryLoading}
                    isAgent={isAgent}
                />
                <RiskDetails
                    detailsData={riskData?.details || []}
                    print={printReport}
                    isLoading={isLoading}
                    isAgent={isAgent}
                    commercialAuto={riskData?.commercialAutoEnabled === true}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onSearchChange={handleSearchChange}
                    onSortChange={handleSortChange}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    minSearchLength={MIN_SEARCH_LENGTH}
                    maxSearchLength={MAX_SEARCH_LENGTH}
                />
            </div>
        </Container>
    )
}

export default RiskDashboard